/**
 * Identity Engine 状态徽章组件
 * 用于显示身份状态、声音状态、语料类型等
 */

import { cn } from '@/shared/lib/utils'
import { Badge } from '@/shared/components/ui/badge'
import { Loader2 } from 'lucide-react'
import {
  IdentityStatus,
  VoiceStatus,
  CorpusType,
  IDENTITY_STATUS_CONFIG,
  VOICE_STATUS_CONFIG,
  CORPUS_TYPE_CONFIG,
} from '@/modules/identity/types/identity'

// ==================== Identity 状态徽章 ====================

interface IdentityStatusBadgeProps {
  status: IdentityStatus
  className?: string
  showIcon?: boolean
  size?: 'sm' | 'md'
}

export function IdentityStatusBadge({
  status,
  className,
  showIcon = true,
  size = 'md',
}: IdentityStatusBadgeProps) {
  // 防御性编程：如果 status 不在配置中，使用默认值
  const config = IDENTITY_STATUS_CONFIG[status] || {
    label: status || '未知',
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
    icon: '❓',
  }
  const isLearning = status === 'learning'

  return (
    <Badge
      variant="outline"
      className={cn(
        'font-normal border-0 gap-1',
        config.bgColor,
        config.color,
        size === 'sm' && 'text-[10px] px-1.5 py-0 h-5',
        size === 'md' && 'text-xs px-2 py-0.5',
        className
      )}
    >
      {showIcon && (
        isLearning ? (
          <Loader2 className={cn('animate-spin', size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3')} />
        ) : (
          <span className={size === 'sm' ? 'text-[10px]' : 'text-xs'}>{config.icon}</span>
        )
      )}
      {config.label}
    </Badge>
  )
}

// ==================== Voice 状态徽章 ====================

interface VoiceStatusBadgeProps {
  status: VoiceStatus
  className?: string
  showIcon?: boolean
  size?: 'sm' | 'md'
}

export function VoiceStatusBadge({
  status,
  className,
  showIcon = true,
  size = 'md',
}: VoiceStatusBadgeProps) {
  // 防御性编程：如果 status 不在配置中，使用默认值
  const config = VOICE_STATUS_CONFIG[status] || {
    label: status || '未知',
    color: 'text-gray-400',
    icon: '❓',
  }
  const isTraining = status === 'training'

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1',
        config.color,
        size === 'sm' && 'text-[10px]',
        size === 'md' && 'text-xs',
        className
      )}
    >
      {showIcon && (
        isTraining ? (
          <Loader2 className={cn('animate-spin', size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3')} />
        ) : (
          <span>{config.icon}</span>
        )
      )}
      {config.label}
    </span>
  )
}

// ==================== Corpus 类型徽章 ====================

interface CorpusTypeBadgeProps {
  type: CorpusType
  className?: string
  showIcon?: boolean
}

export function CorpusTypeBadge({
  type,
  className,
  showIcon = true,
}: CorpusTypeBadgeProps) {
  // 防御性编程：如果 type 不在配置中，使用默认值
  const config = CORPUS_TYPE_CONFIG[type] || {
    label: type || '未知',
    icon: '📄',
  }

  return (
    <Badge
      variant="secondary"
      className={cn('font-normal gap-1 text-xs', className)}
    >
      {showIcon && <span>{config.icon}</span>}
      {config.label}
    </Badge>
  )
}

// ==================== 学习进度条 ====================

interface LearningProgressProps {
  progress: number
  stage?: string
  className?: string
  showLabel?: boolean
}

export function LearningProgress({
  progress,
  stage,
  className,
  showLabel = true,
}: LearningProgressProps) {
  return (
    <div className={cn('space-y-1', className)}>
      {showLabel && (
        <div className="flex justify-between text-xs">
          <span className="text-slate-500">{stage || '学习中'}</span>
          <span className="text-violet-600 font-medium">{progress}%</span>
        </div>
      )}
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full transition-all duration-500"
          style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
        />
      </div>
    </div>
  )
}

// ==================== 简化版进度条（用于卡片） ====================

interface CompactProgressProps {
  progress: number
  className?: string
}

export function CompactProgress({ progress, className }: CompactProgressProps) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div className="flex-1 h-1 bg-slate-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-violet-500 rounded-full transition-all duration-300"
          style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
        />
      </div>
      <span className="text-[10px] text-violet-600 font-medium w-7 text-right">
        {progress}%
      </span>
    </div>
  )
}
